import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  useShellContexts,
  type EntityType,
  type ScreenContext,
} from '@cowbell/moo';
import { platformPersonasRoutemap } from '@cowbell/data/statics';
import {
  extractProductFromRoute,
  useEntityLabel,
} from '../hooks/useEntityLabel';
import { useConvertedPolicyId } from '../hooks/useConvertedPolicyId';
import { useActivePageTab } from '../../tabs/v2/active-tab-store';
import {
  useOpenCmailCategoryTab,
  useOpenCmailMessageId,
} from './cmail-selection-store';

const PERSONAS = Object.values(platformPersonasRoutemap).join('|');

interface DetailRule {
  pattern: RegExp;
  view: string;
  entityType: EntityType;
  /** The captured id is a quote, but the page shows the policy it bound to. */
  policyFromQuote?: boolean;
}

interface ListRule {
  pattern: RegExp;
  view: string;
}

/**
 * Entity-detail patterns. Capture group 1 is the entityId (UUID or slug).
 * These are tried before list-view patterns so the more specific detail
 * URL wins (e.g. /submissions/summary/<UUID> matches detail, not the
 * list-view /submissions fallback).
 *
 * Mirrors V1's ROUTE_RULES (`apps/console/src/components/ask-moo/context/
 * AskMooProvider.tsx`) for the entity types V2 cares about today.
 */
const DETAIL_PATTERNS: readonly DetailRule[] = [
  {
    pattern: new RegExp(String.raw`^/(?:${PERSONAS})/quote/details?/([\w-]+)`),
    view: 'Quotes',
    entityType: 'quote',
  },
  {
    // P100 edit-quote workflow. Admin shape ends /<quoteId>/edit/<tab>;
    // agency shape ends /<quoteId>/edit-quote. Capture group must be the
    // QUOTE id (second id segment), not the account id — the mid-edit
    // pricing merge matches the screen entityId against the quote priced.
    pattern: new RegExp(
      String.raw`^/(?:${PERSONAS})/requestQuote/P100/[\w-]+/([\w-]+)/edit(?:-quote)?(?:/|$)`
    ),
    view: 'Quotes',
    entityType: 'quote',
  },
  {
    // Generic quote edit workflow (P250/PX): /quote/edit/<quoteId>/<product>/<tab>
    pattern: new RegExp(String.raw`^/(?:${PERSONAS})/quote/edit/([\w-]+)`),
    view: 'Quotes',
    entityType: 'quote',
  },
  {
    pattern: new RegExp(String.raw`^/(?:${PERSONAS})/claims/detail/([\w-]+)`),
    view: 'Claims',
    entityType: 'claim',
  },
  {
    pattern: new RegExp(
      String.raw`^/(?:${PERSONAS})/(?:policies|policy)/detail/[^/]+/([\w-]+)`
    ),
    view: 'Policies',
    entityType: 'policy',
  },
  {
    // Rating View: /admin/policies/rating/<quoteId>/<product>/<tab>. Keyed on
    // the quote id, so the chip is the quote until it binds and the policy
    // after.
    pattern: new RegExp(
      String.raw`^/(?:${PERSONAS})/policies/rating/([\w-]+)/[\w-]+`
    ),
    view: 'Policies',
    entityType: 'quote',
    policyFromQuote: true,
  },
  {
    // Carrier and reinsurer rating workspace: /carrier/policies/<quoteId>/<product>.
    // Same page as the admin Rating View without the /rating segment.
    pattern: /^\/(?:carrier|reinsurer)\/policies\/(?!detail\/)([\w-]+)\/[\w-]+/,
    view: 'Policies',
    entityType: 'quote',
    policyFromQuote: true,
  },
  {
    pattern: new RegExp(
      String.raw`^/(?:${PERSONAS})/submissions/(?:quotes|requestQuote)/[^/]+/([\w-]+)`
    ),
    view: 'Submissions',
    entityType: 'submission',
  },
  {
    // Generic submissions detail (e.g. /admin/submissions/summary/<UUID>/...).
    // MUST come after the more-specific /submissions/quotes pattern above.
    pattern: new RegExp(
      String.raw`^/(?:${PERSONAS})/submissions/[^/]+/([\w-]+)`
    ),
    view: 'Submissions',
    entityType: 'submission',
  },
  {
    pattern: new RegExp(String.raw`^/(?:${PERSONAS})/account/([\w-]+)`),
    view: 'Accounts',
    entityType: 'account',
  },
  {
    pattern: new RegExp(
      String.raw`^/(?:${PERSONAS})/accounts/details/([\w-]+)`
    ),
    view: 'Accounts',
    entityType: 'account',
  },
  {
    pattern: new RegExp(
      String.raw`^/(?:${PERSONAS})/agencies/details/([\w-]+)`
    ),
    view: 'Agencies',
    entityType: 'agency',
  },
  {
    // UW Inbox quote review — e.g. /admin/inbox/<quoteId>/<product>.
    // Mirrors V1's ROUTE_RULES (`context/AskMooProvider.tsx`).
    pattern: new RegExp(String.raw`^/(?:${PERSONAS})/inbox/([\w-]+)/[^/]+`),
    view: 'UW Inbox',
    entityType: 'uw-review',
  },
];

const LIST_VIEW_PATTERNS: readonly ListRule[] = [
  { pattern: new RegExp(`^/(?:${PERSONAS})/claims`), view: 'Claims' },
  // Endorsements is a sub-route of /policies — must precede the generic
  // policies rule so it resolves to 'Endorsements' not 'Policies'.
  {
    pattern: new RegExp(
      String.raw`^/(?:${PERSONAS})/(?:policies|policy)/endorsements(?:/|\?|$)`
    ),
    view: 'Endorsements',
  },
  {
    pattern: new RegExp(`^/(?:${PERSONAS})/(?:policies|policy)`),
    view: 'Policies',
  },
  { pattern: new RegExp(`^/(?:${PERSONAS})/submissions`), view: 'Submissions' },
  // Binders is a sub-route of /quotes — must precede the generic quotes rule.
  {
    pattern: new RegExp(String.raw`^/(?:${PERSONAS})/quotes/binders(?:/|\?|$)`),
    view: 'Binders',
  },
  { pattern: new RegExp(`^/(?:${PERSONAS})/quotes`), view: 'Quotes' },
  // Concierge / cmail email inbox — MUST precede the generic
  // ``accounts?`` pattern because that pattern lacks an end anchor
  // and would otherwise swallow ``/<persona>/accounts/<id>/cmail``
  // and resolve it as ``Accounts``. inferViewFromPath returns on the
  // first match, so the more-specific cmail patterns must win.
  { pattern: new RegExp(`^/(?:${PERSONAS})/cmail`), view: 'Concierge' },
  {
    pattern: new RegExp(`^/(?:${PERSONAS})/accounts?/[^/]+/cmail`),
    view: 'Concierge',
  },
  { pattern: new RegExp(`^/(?:${PERSONAS})/accounts?`), view: 'Accounts' },
  { pattern: new RegExp(`^/(?:${PERSONAS})/agencies`), view: 'Agencies' },
  { pattern: new RegExp(`^/(?:${PERSONAS})/binders`), view: 'Binders' },
  { pattern: new RegExp(`^/(?:${PERSONAS})/inbox`), view: 'UW Inbox' },
  {
    pattern: new RegExp(String.raw`^/(?:${PERSONAS})/dashboard(?:/|\?|$)`),
    view: 'Dashboard',
  },
  // Broker is the agency persona's catch-all, so anything registered AFTER it
  // can never win for an `/agency/…` route. That placement is load-bearing for
  // the admin-nav rules below — see the note on that block.
  { pattern: /^\/agency\//, view: 'Broker' },
  // OMNI-713 follow-up: the nav entries below had no rule at all, so every one
  // of them produced NO screen context — not merely a missing sub-tab. Their
  // real nav labels are spelled out here rather than left to `humanizeSegment`
  // because the path segment doesn't recover them (`risk-eng-inbox` → "Risk
  // Eng", `premiumprocessors` → "Processors"), and the chat-agent's
  // VIEW_REGISTRY is keyed on the label.
  //
  // These are admin-nav entries, and they sit BELOW the Broker rule on purpose.
  // `/agency/manage/**` is a real 20-route family (users, roles, licenses,
  // commissions, …) that has always resolved to Broker; matching it as "Manage"
  // would silently retag every broker manage page and cost those users the
  // Broker view's own starters and context — a regression well outside this
  // ticket. Ordering after Broker keeps `/agency/*` exactly as it was while
  // `/admin/*` and every other persona still resolve. (Cursor Bugbot, PR
  // #12983.)
  {
    pattern: new RegExp(`^/(?:${PERSONAS})/(?:risk-eng-inbox|riskEng)`),
    view: 'Risk Eng',
  },
  {
    pattern: new RegExp(`^/(?:${PERSONAS})/renewal-submissions`),
    view: 'Renewals',
  },
  { pattern: new RegExp(`^/(?:${PERSONAS})/compliance`), view: 'Compliance' },
  { pattern: new RegExp(`^/(?:${PERSONAS})/carriers`), view: 'Carriers' },
  { pattern: new RegExp(`^/(?:${PERSONAS})/reinsurers`), view: 'Reinsurers' },
  {
    pattern: new RegExp(`^/(?:${PERSONAS})/premiumprocessors`),
    view: 'Processors',
  },
  { pattern: new RegExp(`^/(?:${PERSONAS})/mssp`), view: 'MSSPs' },
  { pattern: new RegExp(`^/(?:${PERSONAS})/payments`), view: 'Payments' },
  { pattern: new RegExp(`^/(?:${PERSONAS})/manage`), view: 'Manage' },
];

/**
 * First path segment after the persona, used by the `humanizeSegment` fallback.
 */
const PERSONA_SEGMENT = new RegExp(String.raw`^/(?:${PERSONAS})/([\w-]+)`);

/**
 * Turn a URL segment into a human view label: `compliance` → "Compliance",
 * `manual-account` → "Manual Account", `requestQuote` → "Request Quote".
 */
export function humanizeSegment(segment: string): string {
  return segment
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export interface RouteMatch {
  view: string;
  entityType?: EntityType;
  entityId?: string;
  policyFromQuote?: boolean;
}

/**
 * Resolve a pathname to a route match, in precedence order:
 *
 *   1. `DETAIL_PATTERNS`  — entity-detail routes (view + entityType + entityId)
 *   2. `LIST_VIEW_PATTERNS` — named list/tab views
 *   3. `humanizeSegment`  — any other in-app route, labelled from its first path
 *      segment
 *
 * Step 3 exists because steps 1-2 are an allowlist, and a page missing from it
 * got NO screen context at all — `useDerivedScreenContext` publishes nothing,
 * so the agent can't say which page the user is on and drops the sub-tab the
 * `TabSubrouter` already published. That silently regressed nine nav entries
 * (all of Compliance, Renewals, Risk Eng and the entire Admin group) when the
 * menu was reorganised; OMNI-713 fixed the stale-state and sub-tab halves of
 * that bug but left this one. The fallback makes coverage the default, so a new
 * nav entry degrades to a slightly less precise label instead of no context.
 *
 * Explicit rules still win: the nav label and the agent's canonical view name
 * disagree in both directions (`/admin/agencies` is nav "Distribution" but view
 * "Agencies"; `/admin/inbox` is nav "Underwriting" but view "UW Inbox"), so
 * steps 1-2 must be consulted before any label is derived from the path.
 *
 * The fallback is anchored to a persona prefix so non-app routes (`/login`,
 * static pages) still resolve to `undefined` and stay chip-less.
 */
export function matchRoute(pathname: string): RouteMatch | undefined {
  for (const rule of DETAIL_PATTERNS) {
    const m = rule.pattern.exec(pathname);
    if (m?.[1]) {
      return {
        view: rule.view,
        entityType: rule.entityType,
        entityId: m[1],
        ...(rule.policyFromQuote ? { policyFromQuote: true } : {}),
      };
    }
  }
  for (const rule of LIST_VIEW_PATTERNS) {
    if (rule.pattern.test(pathname)) {
      return { view: rule.view };
    }
  }
  const segment = PERSONA_SEGMENT.exec(pathname)?.[1];
  if (segment) {
    return { view: humanizeSegment(segment) };
  }
  return undefined;
}

/**
 * Back-compat for the existing unit test that just wants the view name.
 */
export function inferViewFromPath(pathname: string): string | undefined {
  return matchRoute(pathname)?.view;
}

/**
 * True when `pathname` is governed by the tab bar mounted at `base`. A
 * `TabSubrouter` only ever routes `base` (index tab) or `base/<tab>` — a single
 * extra segment — so we accept the base itself or exactly one more segment.
 *
 * A plain `startsWith(base)` is too loose: a list bar's base (`/admin/agencies`)
 * is a prefix of an entity-detail route (`/admin/agencies/details/<id>`), so the
 * outgoing list tab could momentarily leak its `subTab` onto the detail page.
 * Detail routes go two-or-more segments deep, so the segment-boundary check
 * rejects them — while still accepting a detail page's OWN tab bar, whose base
 * already includes the id (`/…/details/<id>` + `/<tab>`). (Cursor Bugbot, PR
 * #12907.)
 */
export function isWithinTabBase(pathname: string, base: string): boolean {
  if (pathname === base) return true;
  if (!pathname.startsWith(`${base}/`)) return false;
  // Remainder after `base/`: '' (index with trailing slash) or the tab segment.
  // Anything with a further '/' is a deeper route this tab bar doesn't own.
  return !pathname.slice(base.length + 1).includes('/');
}

/**
 * Hook that subscribes to the current route and pushes a ScreenContext into
 * the shell's derived-context slot. On entity-detail routes it also resolves
 * the entity's friendly number + display name via V1's `useEntityLabel`
 * (React-Query-cached) so the chip can render as
 * "Submission: SUB-VYPO1BI - Nopetro Energy Holdings, LLC" instead of just
 * "submissions: <UUID>".
 *
 * Must be rendered inside both `<MooChatProvider>` (for `useShellContexts`)
 * and a react-router context (for `useLocation`).
 */
export function useDerivedScreenContext(): void {
  const location = useLocation();
  const { setDerived } = useShellContexts();

  const match = useMemo(
    () => matchRoute(location.pathname),
    [location.pathname]
  );

  // Cmail has no detail route — opening an email is local `CmailInbox` state,
  // not a pathname change, so `matchRoute` can never produce an entityType/Id
  // for it the way it does for quote/claim/policy/etc. detail routes. Fold in
  // the open message id (published via the store) on the cmail list view so
  // an open email still resolves to a pinnable `cmail` entity context.
  const openCmailMessageId = useOpenCmailMessageId();
  const isCmailListView = match?.view === 'Concierge';

  // OMNI-192: the cmail category tab rides on the screen context as `subTab`
  // so `/starters` can return tab-specific conversation starters. Like the
  // message id above, it is local CmailInbox state the pathname never reveals.
  const cmailCategoryTab = useOpenCmailCategoryTab();

  // Page-level tab (Agencies → Wholesale, Submissions → Portal, Claims → …),
  // published by the shared `TabSubrouter` which owns the real tab label. These
  // split views all resolve to a single `view`, so without this the agent can't
  // tell which tab the user is on. `isWithinTabBase` confirms the current route
  // is actually governed by the publishing tab bar, so a bar that just unmounted
  // mid-navigation can't attach its stale label to a deeper detail route.
  const { tabLabel, baseUrl: tabBaseUrl } = useActivePageTab();
  const pageTab =
    tabBaseUrl && isWithinTabBase(location.pathname, tabBaseUrl)
      ? tabLabel
      : undefined;

  // cmail's category tab is local inbox state the URL never reveals, so it wins
  // on the Concierge view; every other view uses the TabSubrouter-published tab.
  const subTab = isCmailListView ? cmailCategoryTab : pageTab;

  // Rating View is keyed on a quote id but shows a policy. Once the quote has
  // bound, the chip is that policy; before then it stays the quote.
  const ratingQuoteId = match?.policyFromQuote ? match.entityId : undefined;
  const policyId = useConvertedPolicyId(
    ratingQuoteId,
    ratingQuoteId ? extractProductFromRoute(location.pathname) : undefined
  );
  const routeEntityType: EntityType | undefined = policyId
    ? 'policy'
    : match?.entityType;
  const routeEntityId = policyId ?? match?.entityId;

  // useEntityLabel gates internally on `enabled: !!entityType && !!entityId`,
  // so it's safe to call unconditionally with undefined args on list-view
  // routes — the query simply never fires.
  const entityType =
    routeEntityType ??
    (isCmailListView && openCmailMessageId ? 'cmail' : undefined);
  const entityId =
    routeEntityId ?? (entityType === 'cmail' ? openCmailMessageId : undefined);
  const { data: entityLabel } = useEntityLabel(
    entityType,
    entityId,
    location.pathname
  );

  const resolvedNumber = entityLabel?.number;
  const resolvedName = entityLabel?.name;

  useEffect(() => {
    // routeSignature buckets dismissal memory: dismissing a chip on
    // /admin/quote/details/abc parks the fingerprint under that path,
    // so a re-derive on the same path filters it out. Navigation to a
    // different route uses a different bucket and the dismissal does
    // not carry — V1 used to silently restore on every navigation; the
    // V2 enhancement is to remember dismissals per-route.
    const routeSignature = location.pathname;

    if (!match) {
      setDerived([], routeSignature);
      return;
    }

    const entityData: Record<string, unknown> = {};
    if (resolvedNumber) entityData.number = resolvedNumber;
    if (resolvedName) entityData.name = resolvedName;

    const ctx: ScreenContext = {
      kind: 'screen',
      route: location.pathname,
      view: match.view,
      ...(subTab ? { subTab } : {}),
      ...(entityType ? { entityType } : {}),
      ...(entityId ? { entityId } : {}),
      entityData,
    };
    setDerived([ctx], routeSignature);
  }, [
    location.pathname,
    match,
    subTab,
    entityType,
    entityId,
    resolvedNumber,
    resolvedName,
    setDerived,
  ]);
}
